// @ts-nocheck
/**
 * SuperAdminLayout — shell for the super-admin space.
 *
 * Visual is intentionally aligned with the caller dialer chrome:
 *   - Vertical sidebar (XDial-style: dark surface, tight nav buttons)
 *   - Top header strip with avatar dropdown matching UserHeaderBar
 *   - vf-* design tokens so the look is consistent across both spaces
 *
 * QA-W6-1: below 1024 px (lg) the sidebar, or the rail, folds into a drawer
 * opened by a menu button, like the dialer and the portal; the header keeps
 * the menu button, the current page, the organisation and the avatar
 * (« Contacter le support » moves into the drawer below 640 px). The page
 * never scrolls sideways: wide tables scroll inside their own box.
 *
 * REV-W7-F1: during « ouvrir en tant que » the impersonation banner (with
 * « Quitter ») tops the main column, as in the dialer and the portal: a
 * tenant admin's back office (/AdminPermissions, /team…) is reachable while
 * impersonating. TeamShell reuses this layout for the super admin.
 */

import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import {
  Building2, PhoneCall, Bell, LogOut,
  ShieldCheck, Shield, Voicemail, Server, ArrowLeft, KeyRound, Headphones, Bug, Activity } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/api/api-client';
// "Contacter Kevin" (2026-09-22, pastille retiree le 2026-09-23) : un bouton
// en clair dans l'en-tete, comme sur les autres surfaces.
import { ContactKevinTrigger } from '@/components/common/ContactKevinButton';
import { OrganisationSwitcher } from '@/components/common/OrganisationSwitcher';
import { useState } from 'react';
import { getUiFlags } from '@/lib/flags';
import { AppRail } from '@/components/shell/AppRail';
import { RailDrawer, RailDrawerButton, readRailCollapsed, writeRailCollapsed } from '@/components/shell/RailDrawer';
import { ImpersonationBanner, useImpersonation } from '@/components/shell/ImpersonationBanner';

// `superAdminOnly` entries call super-admin-only APIs and are guarded the same
// way in App.tsx (SUPER_ADMIN_ONLY_PAGES): a tenant_admin does not see them.
// DUP-3 (W6): no « Équipe » entry any more. The super admin opens a team from
// its organisation row (/team, organisation made active); a tenant admin has
// /team in the dialer. QW-h9: Notifications is super admin only.
function getNavItems(t, isSuperAdmin) {
  return [
    { to: '/AdminOrganisations', label: t('admin:superAdminLayout.nav.organisations'), icon: Building2, superAdminOnly: true },
    { to: '/TwilioConfig',       label: t('admin:superAdminLayout.nav.twilio'),        icon: PhoneCall, superAdminOnly: true },
    { to: '/AdminNotifications', label: t('admin:superAdminLayout.nav.notifications'), icon: Bell, superAdminOnly: true },
    { to: '/AdminAmdEngine',     label: t('admin:superAdminLayout.nav.amdEngine'),     icon: Voicemail, superAdminOnly: true },
    { to: '/AdminCallingProvider', label: t('admin:superAdminLayout.nav.callingProvider'), icon: Server, superAdminOnly: true },
    { to: '/AdminAuditLogs',     label: t('admin:superAdminLayout.nav.auditLogs'),     icon: Shield, superAdminOnly: true },
    // W7 (SU-6, SU-7): also for a tenant admin, on its own organisation.
    { to: '/AdminPermissions',   label: t('admin:superAdminLayout.nav.permissions'),   icon: KeyRound },
    { to: '/AdminSupervision',   label: t('admin:superAdminLayout.nav.supervision'),   icon: Headphones },
    { to: '/AdminClientErrors',  label: t('admin:superAdminLayout.nav.clientErrors'),  icon: Bug, superAdminOnly: true },
    { to: '/AdminPlatformHealth', label: t('admin:superAdminLayout.nav.platformHealth'), icon: Activity, superAdminOnly: true },
  ].filter((item) => isSuperAdmin || !item.superAdminOnly);
}

/**
 * T-h5 (W6): one breadcrumb per route, never « Tableau de bord ». `section`
 * is the menu entry lit for the route (a team is reached from Organisations,
 * the Twilio diagnostic from Twilio). Parents are links.
 */
export function breadcrumbFor(pathname, t, activeOrgName) {
  const nav = (k) => t(`admin:superAdminLayout.nav.${k}`);
  const crumb = (k) => t(`admin:superAdminLayout.crumbs.${k}`);
  const orgs = { label: nav('organisations'), to: '/AdminOrganisations' };
  const team = { label: activeOrgName ? `${crumb('team')} · ${activeOrgName}` : crumb('team') };
  const routes = [
    ['/AdminOrganisations', '/AdminOrganisations', [{ label: nav('organisations') }]],
    ['/team', '/AdminOrganisations', [orgs, team]],
    // QA-W6-7: the address names an organisation that may not be the active one: no name.
    ['/organisations/', '/AdminOrganisations', [orgs, { label: crumb('team') }]],
    ['/TwilioConfig', '/TwilioConfig', [{ label: nav('twilio') }]],
    ['/AdminNotifications', '/AdminNotifications', [{ label: nav('notifications') }]],
    ['/AdminAmdEngine', '/AdminAmdEngine', [{ label: nav('amdEngine') }]],
    ['/AdminCallingProvider', '/AdminCallingProvider', [{ label: nav('callingProvider') }]],
    ['/AdminAuditLogs', '/AdminAuditLogs', [{ label: nav('auditLogs') }]],
    ['/AdminPermissions', '/AdminPermissions', [{ label: nav('permissions') }]],
    ['/AdminSupervision', '/AdminSupervision', [{ label: nav('supervision') }]],
    ['/AdminClientErrors', '/AdminClientErrors', [{ label: nav('clientErrors') }]],
    ['/AdminPlatformHealth', '/AdminPlatformHealth', [{ label: nav('platformHealth') }]],
  ];
  const hit = routes.find(([prefix]) => pathname.startsWith(prefix));
  return hit ? { section: hit[1], crumbs: hit[2] } : { section: null, crumbs: [] };
}

function initialsOf(label) {
  return (label || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')
    .slice(0, 2) || '?';
}

export default function SuperAdminLayout() {
  const { t } = useTranslation(['admin']);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  // T-h6 : ce layout sert aussi le tenant_admin ; il ne doit pas se voir « Super admin ».
  const isSuperAdmin = user?.isSuperAdmin === true;
  const NAV_ITEMS = getNavItems(t, isSuperAdmin);
  const reset = useAuthStore((s) => s.reset);

  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } catch {
      /* noop */
    }
    reset();
    navigate('/Login', { replace: true });
  };

  const activeOrgName = useAuthStore((s) => s.activeMembership?.organisationName ?? null);
  const { section, crumbs } = breadcrumbFor(location.pathname, t, activeOrgName);
  useDocumentTitle(crumbs.length ? crumbs[crumbs.length - 1].label : null);
  const userLabel = user?.email || t('admin:superAdminLayout.defaultUserLabel');
  // SR1-flag : le rail ne remplace la barre latérale que derrière ui_refonte.
  const railOn = getUiFlags().ui_refonte;
  const impersonating = !!useImpersonation();
  const [railCollapsed, setRailCollapsed] = useState(() => readRailCollapsed('admin'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const spaceLabel = isSuperAdmin ? t('admin:superAdminLayout.spaceLabel') : t('admin:superAdminLayout.adminSpaceLabel');
  // The legacy sidebar's links, in the fixed sidebar (lg+) or in the drawer.
  const navLinks = (onPick) => (
    <nav className="flex-1 mt-2 flex flex-col" style={{ gap: 2 }}>
      {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
        <Link
          key={to}
          to={to}
          onClick={onPick}
          className="w-full flex items-center transition-colors"
          aria-current={section === to ? 'page' : undefined}
          style={{
            padding: '7px 10px',
            borderRadius: 8,
            background: section === to ? 'var(--vf-accent-bg)' : 'transparent',
            color: section === to ? 'var(--vf-accent)' : 'var(--vf-text-2)',
            gap: 8,
            fontSize: 12,
            fontWeight: section === to ? 600 : 500,
            textDecoration: 'none',
          }}
        >
          <Icon size={13} />
          <span>{label}</span>
        </Link>
      ))}
    </nav>
  );
  const brand = (
    <div className="flex items-center" style={{ gap: 8, marginBottom: 6, padding: '2px 4px' }}>
      <div
        className="flex items-center justify-center"
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          background: 'var(--vf-accent-bg)',
        }}
      >
        <ShieldCheck size={14} style={{ color: 'var(--vf-accent)' }} />
      </div>
      <span
        className="flex-1"
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--vf-text-1)',
          letterSpacing: '-0.01em',
        }}
      >
        {isSuperAdmin ? t('admin:superAdminLayout.brand') : t('admin:superAdminLayout.adminBrand')}
      </span>
    </div>
  );
  const rail = (collapsed, onPick) => (
    <AppRail
      ariaLabel={spaceLabel}
      collapsed={collapsed}
      collapsible={!onPick}
      onCollapsedChange={(c) => { setRailCollapsed(c); writeRailCollapsed('admin', c); }}
      onBrandClick={() => { onPick?.(); navigate(NAV_ITEMS[0]?.to ?? '/'); }}
      secondary={isSuperAdmin ? undefined : { label: t('admin:superAdminLayout.backToDialer'), icon: ArrowLeft, onSelect: () => { onPick?.(); navigate('/ParallelDialer/home'); } }}
      footer={(open) => (open ? <div className="sm:hidden"><ContactKevinTrigger variant="ghost" size="sm" /></div> : null)}
      groups={[NAV_ITEMS.map(({ to, label, icon }) => ({
        id: to,
        label,
        icon,
        active: section === to,
        onSelect: () => { onPick?.(); navigate(to); },
      }))]}
    />
  );

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div
      className="min-h-screen flex overflow-x-clip"
      style={{ background: 'var(--vf-bg)', color: 'var(--vf-text-1)' }}
    >
      {railOn && <div className="hidden lg:block sticky top-0 h-screen">{rail(railCollapsed)}</div>}
      {/* QA-W6-1: below lg, the menu lives in a drawer (both shells). */}
      <RailDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title={spaceLabel}>
        {railOn ? rail(false, closeDrawer) : (
          <div className="flex h-full flex-col" style={{ padding: '12px 10px', gap: 3 }} data-testid="admin-drawer-nav">
            {brand}
            {navLinks(closeDrawer)}
            <div className="sm:hidden"><ContactKevinTrigger variant="ghost" size="sm" /></div>
          </div>
        )}
      </RailDrawer>
      {/* ── Sidebar (mirror of XDialSidebar shape), lg and up ─────────────── */}
      {!railOn && <aside
        className="hidden lg:flex flex-col h-screen sticky top-0 flex-shrink-0 border-r"
        data-testid="admin-sidebar"
        style={{
          width: 200,
          background: 'var(--vf-surface-1)',
          borderColor: 'var(--vf-border)',
          padding: '12px 10px',
          gap: 3,
        }}
      >
        {brand}
        {navLinks(undefined)}
      </aside>}

      {/* ── Main column ───────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header — same height/feel as UserHeaderBar (40px, vf-bg) */}
        <header
          className={`flex items-center justify-between gap-2 flex-shrink-0 min-w-0 px-3 sm:px-4 ${railOn ? 'h-[52px]' : 'h-[52px] lg:h-10'}`}
          style={{
            background: 'var(--vf-bg)',
            borderBottom: '1px solid var(--vf-border)',
          }}
        >
          <div className="flex items-center gap-2 text-xs min-w-0">
            <RailDrawerButton breakpoint="lg" onOpen={() => setDrawerOpen(true)} />
            {/* T-h5: one label per route; parents are links. */}
            <nav aria-label={t('admin:superAdminLayout.breadcrumb')} className="min-w-0">
              <ol className="flex items-center gap-2 min-w-0">
                <li className="hidden sm:inline" style={{ color: 'var(--vf-text-3)' }}>{spaceLabel}</li>
                {crumbs.map((c, i) => {
                  const last = i === crumbs.length - 1;
                  return (
                    <li key={`${c.label}-${i}`} className={`flex items-center gap-2 min-w-0 ${!last ? 'hidden sm:flex' : ''}`}>
                      <span aria-hidden className="hidden sm:inline" style={{ color: 'var(--vf-text-4)' }}>/</span>
                      {c.to && !last ? (
                        <Link to={c.to} className="hover:underline" style={{ color: 'var(--vf-text-3)' }}>{c.label}</Link>
                      ) : (
                        <span aria-current={last ? 'page' : undefined} className="truncate" style={{ color: 'var(--vf-text-1)', fontWeight: 600 }}>{c.label}</span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>

          <div className="flex items-center min-w-0 shrink-0" style={{ gap: 8 }}>
            {/* Organisation active : la page Équipe en a besoin, et un super admin
                n'en a aucune par défaut (ADMIN-1). La ligne d'organisation la
                règle aussi en ouvrant son équipe (DUP-3). */}
            <OrganisationSwitcher className="max-w-[150px] sm:max-w-[220px]" />
            {/* Cet espace n'a aucune box d'aide : le bouton vit dans l'en-tete. */}
            <div className="hidden sm:block"><ContactKevinTrigger variant="ghost" size="sm" /></div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-11 w-11 shrink-0 items-center justify-center cursor-pointer transition-opacity hover:opacity-85"
                  style={{ background: 'transparent', border: 'none' }}
                  title={userLabel}
                  aria-label={t('admin:superAdminLayout.accountMenu', { user: userLabel })}
                >
                  {/* QA-W6-9: the initials are decoration; the label names the account. */}
                  <span
                    aria-hidden
                    className="flex items-center justify-center"
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 15,
                      background: 'var(--vf-primary)',
                      color: 'var(--vf-primary-fg)',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {initialsOf(userLabel.split('@')[0])}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <div
                  className="px-4 py-3 border-b"
                  style={{ borderColor: 'var(--vf-border)' }}
                >
                  <p className="text-sm font-bold">{userLabel}</p>
                  <p
                    className="text-[11px] mt-0.5"
                    style={{ color: 'var(--vf-text-3)' }}
                  >
                    {isSuperAdmin ? t('admin:superAdminLayout.roleLabel') : t('admin:superAdminLayout.tenantAdminRole')}
                  </p>
                </div>
                <DropdownMenuSeparator />
                {!isSuperAdmin && (
                  // T-h6 : un tenant_admin a un espace à lui, le dialer ; il y retourne d'ici.
                  <DropdownMenuItem onClick={() => navigate('/ParallelDialer/home')} className="cursor-pointer text-xs gap-2">
                    <ArrowLeft size={13} />
                    {t('admin:superAdminLayout.backToDialer')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-xs gap-2"
                  style={{ color: 'var(--vf-red)' }}
                >
                  <LogOut size={13} />
                  {t('admin:superAdminLayout.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {impersonating && (
          <div className="px-4 pt-3 sm:px-6">
            <ImpersonationBanner />
          </div>
        )}

        {/* Page body */}
        <main className="flex-1 min-w-0 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
